import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import {
  ARTICLE_RIGHT_RAIL_PAGE_TYPE,
  ARTICLE_SLIDESHOW_PAGE_TYPE,
  ARTICLE_FULL_WIDTH_PAGE_TYPE,
  ARTICLE_CUSTOM_PAGE_TYPE,
  POST_ARTICLE_SLIDESHOW_PAGE_TYPE,
  SPOTIM_WORK_AND_MONEY_CATEGORY,
  SPOTIM_ENTERTAINMENT_CATEGORY,
  SPOTIM_STYLE_CATEGORY,
  SPOTIM_BEAUTY_CATEGORY,
  SPOTIM_HEALTH_CATEGORY,
  SPOTIM_FOOD_CATEGORY,
  SPOTIM_US_NEWS_PRIMARY_CATEGORY,
  ARTICLE_MAIN_CONTENT_NODE_ID,
  EDITORIAL_CONTENT_NODE_ID,
  SOLD_CONTENT_TYPES,
  EDITION_ID_EN_US
} from '../constants';
import ProductGrid from './article/products/product_grid';
import ArticleHeader from './article/article_header';
import ArticleFooter from './article/article_footer';
import ChapterNavigation from './article/chapter_navigation';
import CondensedHeader from './article/condensed_header';
import HeroImage from './article/hero_image';
import PrimaryTagBanner from './article/primary_tag_banner';
import Slideshow from './article/slideshow';
import RightRailAd from './article/right_rail_ad';
import Comments from './article/comments';
import Recirculation from './article/recirculation';
import Sections from './section/sections';
import Ad from './common/ad';
import MediaQuery from './common/media_query';
import SocialIcons from './common/social_icons';
import classNames from 'classnames';

import { get, trim, isNil, omitBy, compact, isEmpty, partial, partition } from 'lodash';
import {
  getSectionAdSizesForArticleType,
  ABOVE_ARTICLE_HEADER_AD_SIZES
} from '../utils/ads/sizes';
import { getMainAssetAltText } from '../utils/images';
import { disableOpenWebAds } from '../utils/ads/webAdLib';

const HEADER_AD_REFRESH_RATE_PER_SLIDE = 1;

export default class Article extends PureComponent {
  componentDidMount() {
    if (this.hasAdSponsorship()) {
      disableOpenWebAds();
    }
  }

  hasAdSponsorship = () =>
    trim(get(this.props, 'adsState.ad_data.ad_sponsorship', '')).length > 0;

  render() {
    const {
      meta: {
        product_layout,
        social_title,
        campaign_branding_clicktracker,
        campaign_branding_impressiontracker,
        full_intro_entry,
        slideshow_full,
        program_branding_click_tracker,
        program_branding_impression_tracker,
        campaign_branding_nofollow,
        program_branding_nofollow,
        sold_content_type,
        hide_product_info_button
      },
      id,
      type,
      asset_main,
      opener_images,
      isInitialPageview,
      truncated_short_title_80,
      excerpt_truncated_325,
      primary_tag,
      original_published,
      published,
      title,
      breadcrumbs,
      url_full,
      adsState,
      campaign_branding,
      campaign_branding_image_mobile_url,
      sanitizedTitle,
      asset_opener_article_full,
      asset_opener_intro_slide_override,
      program_branding,
      program_branding_image_url,
      sections,
      related_entries,
      comments_id,
      changeSlide,
      upNext,
      pickBridgeRecommendation,
      recommendations,
      bylines,
      spotID,
      meta_description,
      meta_title,
      primary_category,
      dek,
      isShoppingArticle
    } = this.props;

    const { edition_data } = this.context;

    const [featuredBylines, nonFeaturedBylines] = partition(
      bylines,
      'featured'
    );
    const enableAdTextForShares =
      sold_content_type === SOLD_CONTENT_TYPES.CUSTOM;
    const articleClassName = classNames(
      'r29-article',
      getClassNameForType(type)
    );
    const onSlideshowArticle = isSlideshowArticle(type);
    const onRightRailArticle = isRightRailArticle(type);
    const onFullWidthArticle = isFullWidthArticle(type);
    const onCustomArticle = isCustomArticle(type);

    const socialIconProps = {
      asset_main,
      opener_images,
      enableAdTextForShares,
      sanitizedTitle,
      url_full,
      meta_description,
      meta_title,
      social_title,
      excerpt_truncated_325
    };

    const headerSocialIconProps = {
      parentClass: 'header',
      ...socialIconProps
    };

    const shouldDisplayDek = !!(onFullWidthArticle && dek);

    const articleHeaderProps = {
      original_published,
      published,
      title,
      bylines: featuredBylines,
      campaign_branding,
      campaign_branding_image_mobile_url,
      campaign_branding_clicktracker,
      campaign_branding_impressiontracker,
      campaign_branding_nofollow,
      program_branding,
      program_branding_image_url,
      program_branding_click_tracker,
      program_branding_impression_tracker,
      program_branding_nofollow,
      socialIconProps: headerSocialIconProps,
      dek,
      shouldDisplayDek
    };

    const condensedHeaderProps = {
      title,
      truncated_short_title_80,
      onSlideshowArticle,
      ...headerSocialIconProps
    };

    const articleFooterProps = omitBy(
      {
        featuredBylines,
        nonFeaturedBylines,
        breadcrumbs,
        published,
        meta_title,
        original_published,
        openerImageCredit: get(opener_images, '[0].asset.credit')
      },
      isNil
    );

    const sectionsProps = {
      adsState,
      adSizes: getSectionAdSizesForArticleType(type),
      enableAdTextForShares,
      isInitialPageview,
      url_full,
      meta_description,
      meta_title,
      social_title,
      sanitizedTitle,
      product_layout,
      hide_product_info_button,
      excerpt_truncated_325,
      sold_content_type,
      onSlideshowArticle,
      onRightRailArticle,
      onFullWidthArticle,
      onCustomArticle,
      isShoppingArticle
    };

    const commentsProps = {
      comments_id,
      url_full,
      primary_category,
      spotID
    };

    const recirculationProps = {
      primary_category,
      spotID
    };

    /*
     * here we use hasNull because some of the items in the bridge response
     * for related links has been observed to be null on cloud boxes.
     */
    const hasNull = (target) => {
      let member;
      for (member in target) {
        if (target[member] === null) {
          return true;
        }
      }
      return false;
    };

    const filteredRelatedStories = !isEmpty(related_entries)
      ? related_entries.filter((item) => !hasNull(item))
      : [];

    let content;
    if (onSlideshowArticle) {
      content = (
        <Slideshow
          bodySections={sections.body}
          changeSlide={changeSlide}
          hasFullWidthIntroSlide={full_intro_entry}
          asset_opener_intro_slide_override={asset_opener_intro_slide_override}
          isFullWidth={slideshow_full}
          openerImages={opener_images}
          sectionsProps={sectionsProps}
          slideshowSections={sections.slideshow}
          socialIconProps={socialIconProps}
          contentContainerId={ARTICLE_MAIN_CONTENT_NODE_ID}
          pickBridgeRecommendation={partial(
            pickBridgeRecommendation,
            recommendations,
            id
          )}
          upNext={upNext}
          relatedEntries={filteredRelatedStories}
        />
      );
    } else {
      content = (
        <div id={EDITORIAL_CONTENT_NODE_ID}>
          <Sections
            sections={sections.body}
            {...sectionsProps}
            relatedEntries={filteredRelatedStories}
            shouldSendGAViewEvents
            shouldSendGAScrollDepthEvents
          />
        </div>
      );
    }

    let productGrid;
    if (product_layout) {
      productGrid = (
        <MediaQuery breakpoint="notMobile">
          <ProductGrid
            products={this.props.products}
            sold_content_type={sold_content_type}
            hide_product_info_button={hide_product_info_button}
          />
        </MediaQuery>
      );
    }

    let primaryTagBanner;
    const primaryTagFirstChannel = get(primary_tag, 'channels[0]');
    if (primaryTagFirstChannel && !onCustomArticle) {
      primaryTagBanner = <PrimaryTagBanner {...primaryTagFirstChannel} />;
    }

    let heroImage;
    if (onFullWidthArticle) {
      const heroImageProps = {
        asset_main,
        opener_images,
        asset_opener_article_full,
        alt: getMainAssetAltText(asset_main, opener_images)
      };
      heroImage = <HeroImage {...heroImageProps} />;
    }

    let chapterNavigation;
    const filteredAnchorSections = sections.body.filter(
      (section) => section.type === 'widget:anchor'
    );
    if (!isEmpty(filteredAnchorSections)) {
      chapterNavigation = (
        <ChapterNavigation
          onCustomArticle={onCustomArticle}
          widgetAnchorSections={filteredAnchorSections}
        />
      );
    }

    let rightRailAd;
    if (onRightRailArticle) {
      const disableAdRefresh = !!get(adsState, 'ad_data.ad_disable_ad_refresh');
      rightRailAd = (
        <MediaQuery breakpoint="desktop">
          <RightRailAd
            adsState={adsState}
            shouldRefreshAdOnInterval={
              sold_content_type !== SOLD_CONTENT_TYPES.SPONSORED &&
              sold_content_type !== SOLD_CONTENT_TYPES.CUSTOM &&
              !disableAdRefresh
            }
          />
        </MediaQuery>
      );
    }

    const renderSlideshowAd = (position, sizes, uniqueId) => (
      <MediaQuery breakpoint="desktop">
        <div className="slideshow-ad-container">
          <Ad
            adsState={adsState}
            sizes={sizes}
            className="ss-ad"
            position={position}
            uniqueId={uniqueId}
            refreshEveryNSlides={HEADER_AD_REFRESH_RATE_PER_SLIDE}
            slideshow={onSlideshowArticle}
            onFullWidthArticle={slideshow_full}
          />
        </div>
     </MediaQuery>
    )

    let aboveArticleHeaderAd;
    let postArticleAd;
    if (onSlideshowArticle) {
      aboveArticleHeaderAd = renderSlideshowAd("top", ABOVE_ARTICLE_HEADER_AD_SIZES, "ss-top");
      postArticleAd = renderSlideshowAd("bottom", POST_ARTICLE_SLIDESHOW_PAGE_TYPE, "ss-bottom");
    }

    const baseSocialIcons = compact([
      'facebook',
      'twitter',
      edition_data.id === EDITION_ID_EN_US && 'flipboard',
      'email'
    ]);

    const articleBottomSocialShare = (
      <div className="share-bottom-wrapper">
        <MediaQuery breakpoint="notMobile">
          <SocialIcons
            {...socialIconProps}
            className={classNames('article-bottom', {
              'two-rows': edition_data.id === EDITION_ID_EN_US
            })}
            icons={baseSocialIcons}
          />
        </MediaQuery>
        <MediaQuery breakpoint="mobile">
          <SocialIcons
            {...socialIconProps}
            className={classNames('article-bottom', {
              'two-rows': edition_data.id === EDITION_ID_EN_US
            })}
            icons={[...baseSocialIcons, 'whatsapp', 'sms']}
          />
        </MediaQuery>
      </div>
    );

    const articleInvusion = (
      <div >
        <Ad
          adsState={adsState}
          sizes={[[1, 5]]}
          position="invusion"
          uniqueId="article-invusion"
          showAdCallout={false}
        />
      </div>
    );

    let recirculationModule;
    if (
      !onCustomArticle &&
      shouldRenderRecirculationModule(
        spotID,
        get(this.props, 'primary_category')
      )
    ) {
      recirculationModule = <Recirculation {...recirculationProps} />;
    }

    const shouldDisplayArticleBottomModules = !onCustomArticle;
    const shouldDisplayCondensedHeader = !onCustomArticle;

    return (
      <div>
        {shouldDisplayCondensedHeader && (
          <MediaQuery breakpoint="desktop">
            <CondensedHeader {...condensedHeaderProps} />
          </MediaQuery>
        )}
        <article className={`r29-article-container ${this.hasAdSponsorship() ? 'sponsored-override' : ''}`}>
          {!onCustomArticle && primaryTagBanner}
          {heroImage}
          {chapterNavigation}
          <div className={articleClassName}>
            {rightRailAd}
            {!onCustomArticle && <ArticleHeader {...articleHeaderProps} />}
            {aboveArticleHeaderAd}
            {articleInvusion}
            <div
              id={ARTICLE_MAIN_CONTENT_NODE_ID}
              className={classNames({ 'full-width': slideshow_full })}
            >
              {content}
              {/* this "content-bottom" id is needed to help control the behavior of the right rail ad on all the article pages */}
              <div id="content-bottom"></div>
              {productGrid}
              {postArticleAd}
              {shouldDisplayArticleBottomModules && articleBottomSocialShare}
              {shouldDisplayArticleBottomModules && (
                <ArticleFooter {...articleFooterProps} />
              )}
              {!this.hasAdSponsorship() &&
                shouldDisplayArticleBottomModules &&
                spotID && <Comments {...commentsProps} />}
              {/* Pubfeed widget */}
              {/*{!onSlideshowArticle && !this.hasAdSponsorship() && <div className="linkby-widget" data-type="listicle" />} */}
            </div>
          </div>
        </article>
        {recirculationModule}
      </div>
    );
  }
}

function getClassNameForType(type) {
  switch (type) {
    case ARTICLE_RIGHT_RAIL_PAGE_TYPE:
      return 'right-rail-article';
    case ARTICLE_SLIDESHOW_PAGE_TYPE:
      return 'slideshow-article';
    case ARTICLE_CUSTOM_PAGE_TYPE:
      return 'custom';
    default:
      return null;
  }
}

function isFullWidthArticle(type) {
  return type === ARTICLE_FULL_WIDTH_PAGE_TYPE;
}

function isRightRailArticle(type) {
  return type === ARTICLE_RIGHT_RAIL_PAGE_TYPE;
}

function isSlideshowArticle(type) {
  return type === ARTICLE_SLIDESHOW_PAGE_TYPE;
}

function isCustomArticle(type) {
  return type === ARTICLE_CUSTOM_PAGE_TYPE;
}

function shouldRenderRecirculationModule(spotID, primaryCategory) {
  // primaryCategory maybe null in cases such as preview of unpublished article
  if (!spotID || !primaryCategory) {
    return false;
  }
  // Check to see if the primary_category id or parent_id is one of the 7 fixed categories are set
  return [
    SPOTIM_WORK_AND_MONEY_CATEGORY,
    SPOTIM_ENTERTAINMENT_CATEGORY,
    SPOTIM_STYLE_CATEGORY,
    SPOTIM_BEAUTY_CATEGORY,
    SPOTIM_HEALTH_CATEGORY,
    SPOTIM_FOOD_CATEGORY,
    SPOTIM_US_NEWS_PRIMARY_CATEGORY
  ]
    .reduce((acc, val) => acc.concat(val), []) // equivalent to .flat() which is still experimental
    .some((x) => {
      return [primaryCategory.id, primaryCategory.parent_id].includes(x);
    });
}

Article.propTypes = {
  id: PropTypes.number,
  comments_id: PropTypes.string,
  adsState: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
  primary_tag: PropTypes.shape({
    id: PropTypes.number.isRequired,
    channels: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired
      })
    ).isRequired
  }),
  original_published: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  published: PropTypes.number.isRequired,
  campaign_branding: PropTypes.object,
  campaign_branding_image_mobile_url: PropTypes.string,
  program_branding: PropTypes.object,
  program_branding_image_url: PropTypes.string,
  sanitizedTitle: PropTypes.string.isRequired,
  isInitialPageview: PropTypes.bool.isRequired,
  isSensitiveContent: PropTypes.bool.isRequired,
  excerpt_truncated_325: PropTypes.string.isRequired,
  truncated_short_title_80: PropTypes.string.isRequired,
  sections: PropTypes.shape({
    body: PropTypes.arrayOf(PropTypes.object),
    slideshow: PropTypes.arrayOf(PropTypes.object)
  }).isRequired,
  related_entries: PropTypes.array,
  meta: PropTypes.shape({
    product_layout: PropTypes.bool,
    social_title: PropTypes.string,
    full_intro_entry: PropTypes.bool,
    slideshow_full: PropTypes.bool,
    campaign_branding_clicktracker: PropTypes.string,
    campaign_branding_impressiontracker: PropTypes.string,
    campaign_branding_nofollow: PropTypes.bool,
    program_branding_click_tracker: PropTypes.string,
    program_branding_impression_tracker: PropTypes.string,
    program_branding_nofollow: PropTypes.bool,
    hide_product_info_button: PropTypes.bool
  }).isRequired,
  newsletters: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,
  primary_category: PropTypes.object,
  products: PropTypes.arrayOf(PropTypes.object),
  type: PropTypes.string.isRequired,
  asset_main: PropTypes.object.isRequired,
  url_full: PropTypes.string.isRequired,
  asset_opener_article_full: PropTypes.shape({
    src_full: PropTypes.string
  }),
  asset_opener_intro_slide_override: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
    src_full: PropTypes.string,
    credit: PropTypes.string
  }),
  opener_images: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      asset: PropTypes.shape({
        credit: PropTypes.string,
        src: PropTypes.shape({
          full: PropTypes.string
        })
      }).isRequired
    })
  ).isRequired,
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired
    })
  ).isRequired,
  changeSlide: PropTypes.func.isRequired,
  pickBridgeRecommendation: PropTypes.func.isRequired,
  upNext: PropTypes.object.isRequired,
  recommendations: PropTypes.arrayOf(PropTypes.object).isRequired,
  bylines: PropTypes.arrayOf(PropTypes.object.isRequired),
  spotID: PropTypes.string,
  meta_title: PropTypes.string,
  meta_description: PropTypes.string,
  channels: PropTypes.arrayOf(PropTypes.string),
  dek: PropTypes.string,
  isShoppingArticle: PropTypes.bool,
  geolocation: PropTypes.string
};

Article.defaultProps = {
  adsState: {},
  campaign_branding: {},
  campaign_branding_image_mobile_url: '',
  program_branding: {},
  program_branding_image_url: '',
  title: '',
  asset_main: {},
  sections: { body: [] },
  url_full: '',
  sanitizedTitle: '',
  isInitialPageview: false,
  isSensitiveContent: false,
  isDedicatedFeature: false,
  asset_opener_article_full: { src_full: '' },
  opener_images: [],
  breadcrumbs: [],
  excerpt_truncated_325: '',
  truncated_short_title_80: '',
  original_published: 0,
  primary_tag: {},
  published: 0,
  meta: {},
  dek: ''
};

Article.contextTypes = {
  edition_data: PropTypes.object,
  experiments: PropTypes.object,
  location: PropTypes.object
};
